"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Grid,
  Paper,
  Button,
  Dialog,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Skeleton,
  useTheme,
  useMediaQuery,
  Avatar,
  Fade,
  Backdrop,
} from "@mui/material"
import {
  CheckCircle,
  Cancel,
  Schedule,
  AttachMoney,
  Category,
  CalendarToday,
  Close,
  Image,
  PictureAsPdf,
  InsertDriveFile,
  TrendingUp,
  Assignment,
  AccessTime,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import { getManagerRequisitions, updateRequisitionStatus } from "../../../api/requisition/requisition"
import { format } from "date-fns"
import { IonPage, IonContent } from "@ionic/react"
import { formatCurrencyN } from "../../../common/utils_t"

interface Requisition {
  id: string
  requisition_id: string
  user_id: string
  user_name: string
  title: string
  description: string
  amount: number
  status: string
  expense_date: string
  category: string
  attachment_path?: string
  created_at: string
}

// Styled components with clean aesthetics
const StyledCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  border: "none",
  borderRadius: 16,
  background: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(10px)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
  },
}))

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => ({
  fontWeight: 600,
  borderRadius: 20,
  fontSize: "0.75rem",
  height: 28,
  ...(status === "approved" && {
    background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
    color: "white",
  }),
  ...(status === "rejected" && {
    background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
    color: "white",
  }),
  ...(status === "postponed" && {
    background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
    color: "white",
  }),
  ...(status === "pending" && {
    background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
    color: "white",
  }),
}))

const ActionButton = styled(Button)<{ selected?: boolean; actiontype: string }>(({ theme, selected, actiontype }) => ({
  padding: theme.spacing(1.5, 3),
  borderRadius: 25,
  textTransform: "none",
  fontWeight: 600,
  minWidth: 140,
  fontSize: "0.95rem",
  transition: "all 0.3s ease",
  border: "2px solid transparent",
  ...(selected && {
    transform: "scale(1.05)",
    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
  }),
  ...(actiontype === "approve" && {
    background: selected ? "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)" : "rgba(76, 175, 80, 0.1)",
    color: selected ? "white" : "#4CAF50",
    borderColor: selected ? "transparent" : "#4CAF50",
    "&:hover": {
      background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
      color: "white",
      transform: "translateY(-2px)",
    },
  }),
  ...(actiontype === "reject" && {
    background: selected ? "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)" : "rgba(244, 67, 54, 0.1)",
    color: selected ? "white" : "#f44336",
    borderColor: selected ? "transparent" : "#f44336",
    "&:hover": {
      background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
      color: "white",
      transform: "translateY(-2px)",
    },
  }),
  ...(actiontype === "postpone" && {
    background: selected ? "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)" : "rgba(255, 152, 0, 0.1)",
    color: selected ? "white" : "#ff9800",
    borderColor: selected ? "transparent" : "#ff9800",
    "&:hover": {
      background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
      color: "white",
      transform: "translateY(-2px)",
    },
  }),
}))

const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  marginBottom: theme.spacing(4),
  border: "none",
  boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
}))

const CleanDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 20,
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
    border: "none",
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(20px)",
  },
}))

const ManagerRequisitionsPage: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const [requisitions, setRequisitions] = useState<Requisition[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedRequisition, setSelectedRequisition] = useState<Requisition | null>(null)
  const [action, setAction] = useState<"approve" | "reject" | "postpone" | null>(null)
  const [notes, setNotes] = useState("")
  const [postponeMonth, setPostponeMonth] = useState<number>(new Date().getMonth() + 1)
  const [postponeYear, setPostponeYear] = useState<number>(new Date().getFullYear())
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    fetchRequisitions()
  }, [])

  const fetchRequisitions = async () => {
    setLoading(true)
    try {
      const data = await getManagerRequisitions()
      setRequisitions(data.data)
    } catch (error) {
      console.error("Error fetching requisitions:", error)
    } finally {
      setLoading(false)
    }
  }

  const parseAttachments = (attachmentPath?: string) => {
    if (!attachmentPath) return []
    try {
      return JSON.parse(attachmentPath) as string[]
    } catch {
      return [attachmentPath]
    }
  }

  const handleStatusUpdate = async () => {
    if (!selectedRequisition || !action) return
    setProcessing(true)
    try {
      let statusData: any = {
        status: action,
        manager_notes: notes,
        requisition_id:selectedRequisition.requisition_id
      }

      if (action === "postpone") {
        const postponeDate = new Date(postponeYear, postponeMonth - 1, 1)
        statusData = {
          ...statusData,
          status: "postponed",
          postponed_to: format(postponeDate, "yyyy-MM-dd"),
        }
      }



    const res =   await updateRequisitionStatus(statusData)

    console.log({res})
      await fetchRequisitions()
      handleCloseModal()
    } catch (error) {
      console.error("Error updating requisition:", error)
    } finally {
      setProcessing(false)
    }
  }

  const handleCloseModal = () => {
    setSelectedRequisition(null)
    setAction(null)
    setNotes("")
    setPostponeMonth(new Date().getMonth() + 1)
    setPostponeYear(new Date().getFullYear())
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const getFileIcon = (filename: string) => {
    const extension = filename.toLowerCase().split(".").pop()
    switch (extension) {
      case "pdf":
        return <PictureAsPdf sx={{ fontSize: 40, color: "#f44336" }} />
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <Image sx={{ fontSize: 40, color: "#4CAF50" }} />
      default:
        return <InsertDriveFile sx={{ fontSize: 40, color: "#2196F3" }} />
    }
  }

  const renderSkeletonLoader = () => (
    <Grid container spacing={3} className="pushDown">
      {Array.from({ length: 6 }).map((_, index) => (
        <Grid item xs={12} md={6} lg={4} key={index}>
          <Card sx={{ borderRadius: 4, p: 2 }}>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Skeleton variant="text" width="70%" height={32} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rectangular" width={80} height={28} sx={{ borderRadius: 4 }} />
              </Box>
              <Skeleton variant="text" width="60%" height={20} sx={{ borderRadius: 2 }} />
              <Skeleton variant="text" width="50%" height={20} sx={{ borderRadius: 2 }} />
              <Skeleton variant="text" width="40%" height={20} sx={{ borderRadius: 2 }} />
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  )

  const renderStats = () => {
    if (loading || !requisitions.length) return null

    const totalRequisitions = requisitions.length
    const pendingRequisitions = requisitions.filter((req) => req.status === "pending").length
    const totalAmount = requisitions.reduce((sum, req) => sum + req.amount, 0)

    return (
      <StatsCard elevation={0}>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} sm={4} textAlign="center">
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Assignment sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {totalRequisitions}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                  Total Requests
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} textAlign="center">
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <AccessTime sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {pendingRequisitions}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                  Pending Review
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} textAlign="center">
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <TrendingUp sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {Math.round(totalAmount / 1000)}K
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                  Total Value
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </StatsCard>
    )
  }

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 5 }, (_, i) => currentYear + i)

  return (

    <IonPage className="pushDown">
      <IonContent>

        <Box
          sx={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          {/* Clean Header */}
          <AppBar
            position="sticky"
            elevation={0}
            sx={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              color: "#333",
              borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
            }}
          >
            <Toolbar sx={{ py: 1 }}>
              <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 700, color: "#333" }}>
                Requisition Manager
              </Typography>
            </Toolbar>
          </AppBar>

          <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Statistics */}
            {renderStats()}

            {/* Requisitions Grid */}
            {loading ? (
              renderSkeletonLoader()
            ) : requisitions.length === 0 ? (
              <Paper
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: 4,
                  background: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(10px)",
                  border: "none",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Assignment sx={{ fontSize: 80, color: "#ddd", mb: 2 }} />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: "#666" }}>
                  All Clear!
                </Typography>
                <Typography variant="body1" sx={{ color: "#999", fontSize: "1.1rem" }}>
                  No pending requisitions to review
                </Typography>
              </Paper>
            ) : (
              <Fade in timeout={800}>
                <Grid container spacing={3}>
                  {requisitions.map((req, index) => (
                    <Grid item xs={12} md={6} lg={4} key={req.id}>
                      <Fade in timeout={600 + index * 100}>
                        <StyledCard onClick={() => setSelectedRequisition(req)}>
                          <CardContent sx={{ p: 3 }}>
                            <Stack spacing={2.5}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    color: "#333",
                                    lineHeight: 1.3,
                                    flex: 1,
                                    mr: 2,
                                  }}
                                >
                                Title:{req.title}
                                </Typography>
                                <StatusChip label={req.status} status={req.status} size="small" />
                              </Box>

                              <Stack spacing={1.5}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                  <Avatar sx={{ width: 32, height: 32, bgcolor: "#667eea", fontSize: "0.9rem" }}>
                                    {/* {req.user_name.charAt(0)} */}
                                  </Avatar>
                                  <Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
                                    {req.user_name}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  {/* <AttachMoney sx={{ fontSize: 18, color: "#4CAF50" }} /> */}
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#4CAF50" }}>
                                        {formatCurrencyN(Number(req?.amount))}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                                  {/* <Category sx={{ fontSize: 18, color: "#999" }} /> */}
                                  Category:
                                  <Typography variant="body2" sx={{ color: "#666", textTransform: "capitalize" }}>
                                    {req.category.replace("_", " ")}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <CalendarToday sx={{ fontSize: 18, color: "#999" }} />
                                  <Typography variant="body2" sx={{ color: "#666" }}>
                                    {format(new Date(req.expense_date), "MMM dd, yyyy")}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </StyledCard>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Fade>
            )}

            {/* Clean Modal */}
            <CleanDialog
              open={!!selectedRequisition}
              onClose={handleCloseModal}
              maxWidth="md"
              fullWidth
              fullScreen={isMobile}
              closeAfterTransition
              BackdropComponent={Backdrop}
              BackdropProps={{
                timeout: 500,
                sx: { backdropFilter: "blur(10px)" },
              }}
            >
              <Box sx={{ p: 4 }}>
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: "#333", mb: 1 }}>
                      Title : {selectedRequisition?.title}
                    </Typography>
                    <Typography variant="h5" sx={{ color: "#4CAF50", fontWeight: 600 }}>
                      {formatCurrencyN(Number(selectedRequisition?.amount || 0))}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={handleCloseModal}
                    sx={{
                      bgcolor: "rgba(0, 0, 0, 0.05)",
                      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.1)" },
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>

                <DialogContent sx={{ p: 0, maxHeight: "60vh", overflowY: "auto" }}>
                  {selectedRequisition && (
                    <Stack spacing={4}>
                      {/* Description */}
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
                          Description:
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.8, color: "#666", fontSize: "1.1rem" }}>
                          {selectedRequisition.description}
                        </Typography>
                      </Box>

                      {/* Details Grid */}
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
                          Details
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 2, bgcolor: "rgba(102, 126, 234, 0.05)", borderRadius: 2 }}>
                              <Typography variant="body2" sx={{ color: "#999", mb: 0.5 }}>
                                Requested By:
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "#333" }}>
                                {selectedRequisition.firstName}  {selectedRequisition.middleName}  
                                {selectedRequisition.lastName}  
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 2, bgcolor: "rgba(76, 175, 80, 0.05)", borderRadius: 2 }}>
                              <Typography variant="body2" sx={{ color: "#999", mb: 0.5 }}>
                                Expense Date
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "#333" }}>
                                {format(new Date(selectedRequisition.expense_date), "MMM dd, yyyy")}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 2, bgcolor: "rgba(255, 152, 0, 0.05)", borderRadius: 2 }}>
                              <Typography variant="body2" sx={{ color: "#999", mb: 0.5 }}>
                                Category
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, color: "#333", textTransform: "capitalize" }}
                              >
                                {selectedRequisition.category.replace("_", " ")}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 2, bgcolor: "rgba(33, 150, 243, 0.05)", borderRadius: 2 }}>
                              <Typography variant="body2" sx={{ color: "#999", mb: 0.5 }}>
                                Submitted
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "#333" }}>
                                {format(new Date(selectedRequisition.created_at), "MMM dd, yyyy")}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Attachments */}
                      {selectedRequisition.attachment_path && (
                        <Box>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
                            Attachments
                          </Typography>
                          <Grid container spacing={2}>
                            {parseAttachments(selectedRequisition.attachment_path).map((path, index) => (
                              <Grid item xs={6} sm={4} key={index}>
                                <Paper
                                  onClick={() => window.open(path, "_blank")}
                                  sx={{
                                    p: 3,
                                    textAlign: "center",
                                    cursor: "pointer",
                                    borderRadius: 3,
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                      transform: "translateY(-4px)",
                                      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
                                    },
                                  }}
                                >
                                  {getFileIcon(path)}
                                  <Typography variant="caption" sx={{ mt: 1, display: "block", color: "#666" }}>
                                    {path.split("/").pop()?.substring(0, 15)}...
                                  </Typography>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}

                      <Divider sx={{ my: 2 }} />

                      {/* Actions */}
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 3 }}>
                          Make Decision
                        </Typography>
                        <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mb: 4 }}>
                          <ActionButton
                            actiontype="approve"
                            selected={action === "approve"}
                            onClick={() => setAction("approve")}
                            disabled={processing}
                            startIcon={<CheckCircle />}
                          >
                            Approve
                          </ActionButton>
                          <ActionButton
                            actiontype="reject"
                            selected={action === "reject"}
                            onClick={() => setAction("reject")}
                            disabled={processing}
                            startIcon={<Cancel />}
                          >
                            Reject
                          </ActionButton>
                          <ActionButton
                            actiontype="postpone"
                            selected={action === "postpone"}
                            onClick={() => setAction("postpone")}
                            disabled={processing}
                            startIcon={<Schedule />}
                          >
                            Postpone
                          </ActionButton>
                        </Stack>

                        {/* Postpone Date Selection */}
                        {action === "postpone" && (
                          <Fade in>
                            <Box sx={{ mb: 4, p: 3, bgcolor: "rgba(255, 152, 0, 0.05)", borderRadius: 3 }}>
                              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                                Postpone Until:
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <FormControl fullWidth>
                                    <InputLabel>Month</InputLabel>
                                    <Select
                                      value={postponeMonth}
                                      label="Month"
                                      onChange={(e) => setPostponeMonth(e.target.value as number)}
                                      sx={{ borderRadius: 2 }}
                                    >
                                      {months.map((month, index) => (
                                        <MenuItem key={month} value={index + 1}>
                                          {month}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <FormControl fullWidth>
                                    <InputLabel>Year</InputLabel>
                                    <Select
                                      value={postponeYear}
                                      label="Year"
                                      onChange={(e) => setPostponeYear(e.target.value as number)}
                                      sx={{ borderRadius: 2 }}
                                    >
                                      {years.map((year) => (
                                        <MenuItem key={year} value={year}>
                                          {year}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </Box>
                          </Fade>
                        )}

                        {/* Notes */}
                        <TextField
                          fullWidth
                          label="Notes for Employee"
                          multiline
                          rows={4}
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Enter any notes or feedback..."
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                    </Stack>
                  )}
                </DialogContent>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    mt: 4,
                    pt: 3,
                    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                  }}
                >
                  <Button
                    onClick={handleCloseModal}
                    disabled={processing}
                    sx={{
                      borderRadius: 3,
                      px: 3,
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleStatusUpdate}
                    disabled={!action || processing}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                      },
                    }}
                  >
                    {processing ? "Processing..." : "Submit Decision"}
                  </Button>
                </Box>
              </Box>
            </CleanDialog>
          </Container>
        </Box>
      </IonContent>


    </IonPage>
  )
}

export default ManagerRequisitionsPage